/**
 * Creator Dashboard Types
 *
 * Types for creator-specific API responses:
 * - CreatorTokenWithStats: token metadata + nullable indexer stats
 * - CreatorTokenListResponse: list of creator tokens
 * - CreatorFeesResponse: accumulated fees + claim history
 *
 * Story 6.1 — Task 5
 */

import { z } from "zod";
import {
	AccumulatedFeesSchema,
	DataSourceMetaSchema,
	FeeClaimEntrySchema,
	PriceIntervalSchema,
} from "./indexer";
import { TokenCategorySchema, TokenStatusSchema, VerificationStatusSchema } from "./token";

// ─── Creator Token Types ────────────────────────────────────

/** Stats from IIndexerService for a single token — nullable when indexer is unavailable */
export const CreatorTokenStatsSchema = z.object({
	price: z.number(),
	priceChange24h: z.number(),
	volume24h: z.number(),
	holderCount: z.number(),
	marketCap: z.number(),
	liquidity: z.number(),
	feesUsd24h: z.number(),
	txCount24h: z.number(),
});
export type CreatorTokenStats = z.infer<typeof CreatorTokenStatsSchema>;

export const CreatorTokenWithStatsSchema = z.object({
	id: z.string(),
	name: z.string(),
	symbol: z.string(),
	description: z.string().nullable(),
	tagline: z.string().nullable(),
	category: TokenCategorySchema,
	logoUploadId: z.string().nullable(),
	creatorWallet: z.string(),
	contractAddress: z.string().nullable(),
	poolAddress: z.string().nullable(),
	txHash: z.string().nullable(),
	chainId: z.number(),
	feeSplitCreator: z.number(),
	feeSplitTreasury: z.number(),
	feeSplitProtocol: z.number(),
	status: TokenStatusSchema,
	verificationStatus: VerificationStatusSchema,
	createdAt: z.string(),
	updatedAt: z.string(),
	stats: CreatorTokenStatsSchema.nullable(),
	accumulatedFees: AccumulatedFeesSchema.nullable(),
});
export type CreatorTokenWithStats = z.infer<typeof CreatorTokenWithStatsSchema>;

export const CreatorTokenListResponseSchema = z.object({
	tokens: z.array(CreatorTokenWithStatsSchema),
	meta: DataSourceMetaSchema,
});
export type CreatorTokenListResponse = z.infer<typeof CreatorTokenListResponseSchema>;

// ─── Creator Fees Types ─────────────────────────────────────

export const CreatorFeesResponseSchema = z.object({
	accumulated: AccumulatedFeesSchema.nullable(),
	history: z.array(FeeClaimEntrySchema),
	meta: DataSourceMetaSchema,
});
export type CreatorFeesResponse = z.infer<typeof CreatorFeesResponseSchema>;

// ─── Fee Revenue History Types ──────────────────────────────

/** Numeric string (bigint-safe): digits only — matches indexer.ts numericString */
const numericString = z.string().regex(/^\d+$/, "Must be a numeric string");

export const FeeRevenueHistoryEntrySchema = z.object({
	timestamp: z.number(), // Unix seconds — period start
	claimedAmount: numericString, // bigint as string (wei)
	cumulativeAmount: numericString, // bigint as string (wei)
});
export type FeeRevenueHistoryEntry = z.infer<typeof FeeRevenueHistoryEntrySchema>;

export const FeeRevenueHistoryResponseSchema = z.object({
	data: z.array(FeeRevenueHistoryEntrySchema),
	meta: DataSourceMetaSchema,
});
export type FeeRevenueHistoryResponse = z.infer<typeof FeeRevenueHistoryResponseSchema>;

export const FeeRevenueHistoryQuerySchema = z.object({
	interval: PriceIntervalSchema,
	limit: z.coerce.number().min(1).max(200).default(30),
});
export type FeeRevenueHistoryQueryParams = z.infer<typeof FeeRevenueHistoryQuerySchema>;
